import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import mapper from '../../../../../_endpoints'
import { useFetch } from '../../../../../_hooks/useFetch'
import { appInsights } from '../../../../../_services/AppInsights'
import { EventType } from '../../../../../utility/types'
import { getPreviewCategory, PreviewCategory } from './fileTypeRegistry'
import { CitationLocation, PreviewErrorType, PreviewLoadingState, ResolvedCitation } from './types'

export interface UseDocumentCitationPreviewResult {
  selectedCitation: ResolvedCitation | null
  previewOpen: boolean
  activeHighlightIndex: number
  previewLoadingState: PreviewLoadingState
  highlightedLocations: CitationLocation[]
  previewError: PreviewErrorType
  previewCategory: PreviewCategory | null
  blobUrl: string | null
  openPreview: (
    citation: ResolvedCitation | null,
    triggerElement?: HTMLElement | null,
    targetPageNumber?: number,
    openTrigger?: string
  ) => Promise<void>
  closePreview: () => void
  goToNextHighlight: () => void
  goToPrevHighlight: () => void
  retryLoad: () => void
  downloadFallback: () => void
  handleViewerError: (error: Error) => void
}

/**
 * Encapsulates all state for the document citation preview feature: selectedCitation,
 * previewOpen, activeHighlightIndex, previewLoadingState and highlightedLocations, plus the
 * actions that mutate them. Kept local to the feature (rather than the shared ChatbotContext)
 * so the rest of the chat surface is unaffected by this addition.
 *
 * Documents attached to a chat message are ingested into a databox, not SmartDocs directly, so
 * they're fetched via the same `/api/databox/documents/download/{id}` endpoint the Teams databox
 * download feature already uses (`databoxService.getDocumentBlobUrl`) — not the SmartDocs proxy
 * (`smartflow.document.*`), which 404s for these ids since it's a different document store used
 * by smart-flow's own Qna feature.
 */
export const useDocumentCitationPreview = (): UseDocumentCitationPreviewResult => {
  const userId = useSelector((state: any) => state?.profileReducer?.profile?.email) || 'unknown'
  const [, downloadDocument] = useFetch()

  const [selectedCitation, setSelectedCitation] = useState<ResolvedCitation | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [activeHighlightIndex, setActiveHighlightIndex] = useState(0)
  const [previewLoadingState, setPreviewLoadingState] = useState<PreviewLoadingState>('idle')
  const [previewError, setPreviewError] = useState<PreviewErrorType>(null)
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [previewCategory, setPreviewCategory] = useState<PreviewCategory | null>(null)

  const previousFocusRef = useRef<HTMLElement | null>(null)
  const blobUrlRef = useRef<string | null>(null)
  const loadRequestRef = useRef(0)

  const revokeCurrentBlob = useCallback(() => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current)
      blobUrlRef.current = null
    }
  }, [])

  useEffect(
    () => () => {
      loadRequestRef.current += 1
      revokeCurrentBlob()
    },
    [revokeCurrentBlob]
  )

  const fetchDocumentBlob = useCallback(
    (citation: ResolvedCitation) =>
      downloadDocument(mapper.databox.downloadDocument(citation.fileId), { isBlob: true }),
    [downloadDocument]
  )

  // Emits a single audit event whenever a preview fails to open. Mirrors the CitationPreviewOpened
  // shape (metadata only, never document content) so failures are measurable alongside opens.
  const trackPreviewError = useCallback(
    (
      citation: ResolvedCitation | null,
      errorType: PreviewErrorType,
      extra?: Record<string, any>
    ) => {
      appInsights.trackEvent({
        name: EventType.CitationPreviewError,
        properties: {
          userId,
          fileId: citation?.fileId || '',
          citationId: citation?.citationId,
          fileName: citation?.title,
          previewCategory: citation ? getPreviewCategory(citation.title) : undefined,
          errorType,
          timestamp: new Date().toISOString(),
          ...extra
        }
      })
    },
    [userId]
  )

  // Shared by both a failed blob fetch and a react-pdf parse failure on an already-fetched blob.
  const handleLoadFailure = useCallback(
    (citation: ResolvedCitation | null, stage: 'fetch' | 'render') => {
      setPreviewLoadingState('error')
      setPreviewError('load-failure')
      trackPreviewError(citation, 'load-failure', { stage })
    },
    [trackPreviewError]
  )

  // Fetch the raw document bytes and render them locally (pptx-preview for presentations,
  // react-pdf for PDFs, etc).
  const loadNativeBlob = useCallback(
    async (citation: ResolvedCitation, declaredCategory: PreviewCategory, requestId: number) => {
      try {
        // The databox download endpoint has been verified (live network capture) to ignore any
        // PDF-conversion request and always return the raw file bytes, so PPTX citations fetch
        // through the same single call as every other document rather than paying for a second,
        // full-size download that can never succeed. If backend PDF conversion is added later,
        // reinstate a rendition-specific fetch here rather than assuming the flag already works.
        const blob = await fetchDocumentBlob(citation)
        // useFetch's blob branch doesn't check response.ok, so an error body can come back as a
        // non-empty Blob. Do not reject all text responses: CSV downloads correctly use text/csv.
        // Text/plain is also permitted so the spreadsheet reader can handle services that omit
        // the more specific CSV content type.
        const looksLikeErrorBody = /^(application\/json|text\/html)/i.test(blob?.type || '')
        if (!(blob instanceof Blob) || blob.size === 0 || looksLikeErrorBody) {
          throw new Error('Document blob unavailable')
        }
        if (requestId !== loadRequestRef.current) return
        // Presentation services can return a PDF rendition for a PPTX citation. In that case,
        // use the mature PDF viewer so slide pages, zoom, pagination, and text highlighting all
        // behave exactly like a cited PDF. Native PPTX bytes keep their local slide-text fallback.
        const isPdfRendition = /^application\/pdf$/i.test(blob.type)
        const resolvedCategory = isPdfRendition ? 'pdf' : declaredCategory
        revokeCurrentBlob()
        const typedBlob =
          resolvedCategory === 'pdf' ? blob.slice(0, blob.size, 'application/pdf') : blob
        const objectUrl = URL.createObjectURL(typedBlob)
        blobUrlRef.current = objectUrl
        setBlobUrl(objectUrl)
        setPreviewCategory(resolvedCategory)
        setPreviewLoadingState('loaded')
      } catch (error) {
        if (requestId !== loadRequestRef.current) return
        console.error('Failed to load citation preview document:', error)
        handleLoadFailure(citation, 'fetch')
      }
    },
    [fetchDocumentBlob, revokeCurrentBlob, handleLoadFailure]
  )

  const loadDocument = useCallback(
    async (citation: ResolvedCitation, requestId: number) => {
      if (requestId !== loadRequestRef.current) return
      setPreviewLoadingState('loading')
      setPreviewError(citation.locations.length === 0 ? 'missing-location' : null)

      const declaredCategory = getPreviewCategory(citation.title)

      if (!citation.fileId) {
        if (requestId !== loadRequestRef.current) return
        setPreviewLoadingState('error')
        setPreviewError('missing-document')
        trackPreviewError(citation, 'missing-document')
        return
      }

      if (declaredCategory === 'unsupported') {
        if (requestId !== loadRequestRef.current) return
        setPreviewLoadingState('error')
        setPreviewError('unsupported-file-type')
        trackPreviewError(citation, 'unsupported-file-type')
        return
      }

      await loadNativeBlob(citation, declaredCategory, requestId)
    },
    [loadNativeBlob]
  )

  // Fetch succeeded but react-pdf couldn't parse the bytes (e.g. corrupted or non-PDF content
  // masquerading with a pdf.-like MIME type) — route through the same fallback flow as a fetch
  // failure instead of leaving the panel stuck on a blank/broken render.
  const handleViewerError = useCallback(
    (error: Error) => {
      console.error('Citation document viewer failed to render:', error)
      revokeCurrentBlob()
      setBlobUrl(null)
      if (selectedCitation?.fileId) handleLoadFailure(selectedCitation, 'render')
    },
    [selectedCitation, revokeCurrentBlob, handleLoadFailure]
  )

  const openPreview = useCallback(
    async (
      citation: ResolvedCitation | null,
      triggerElement?: HTMLElement | null,
      targetPageNumber?: number,
      openTrigger?: string
    ) => {
      if (!citation) return

      const requestId = ++loadRequestRef.current

      previousFocusRef.current =
        triggerElement || (document.activeElement as HTMLElement | null) || null

      const requestedPage =
        Number.isInteger(targetPageNumber) && Number(targetPageNumber) > 0
          ? Number(targetPageNumber)
          : undefined
      const matchingLocationIndex = requestedPage
        ? citation.locations.findIndex((location) => Number(location.pageNumber) === requestedPage)
        : -1
      const snippetLocations = citation.locations.filter((location) => location.textSnippet)
      const unambiguousSnippet =
        matchingLocationIndex >= 0
          ? citation.locations[matchingLocationIndex]?.textSnippet
          : snippetLocations.length === 1
          ? snippetLocations[0].textSnippet
          : undefined
      const resolvedCitation = requestedPage
        ? {
            ...citation,
            pageNumber: requestedPage,
            locations:
              matchingLocationIndex >= 0
                ? citation.locations
                : [
                    { pageNumber: requestedPage, textSnippet: unambiguousSnippet },
                    ...citation.locations
                  ]
          }
        : citation

      setSelectedCitation(resolvedCitation)
      setActiveHighlightIndex(matchingLocationIndex >= 0 ? matchingLocationIndex : 0)
      setPreviewOpen(true)
      revokeCurrentBlob()
      setBlobUrl(null)
      setPreviewCategory(null)
      setPreviewLoadingState('loading')
      setPreviewError(null)

      // Fire-and-forget audit event — recorded on open intent, not on successful load, matching
      // the existing IsCitationOpened pattern. Only metadata is captured, never document content.
      const category = getPreviewCategory(resolvedCitation.title)
      appInsights.trackEvent({
        name: EventType.CitationPreviewOpened,
        properties: {
          userId,
          fileId: resolvedCitation.fileId,
          citationId: resolvedCitation.citationId,
          fileName: resolvedCitation.title,
          pageNumber: resolvedCitation.pageNumber,
          previewCategory: category,
          openTrigger: openTrigger || 'unknown',
          timestamp: new Date().toISOString()
        }
      })

      await loadDocument(resolvedCitation, requestId)
    },
    [loadDocument, revokeCurrentBlob, userId]
  )

  const closePreview = useCallback(() => {
    loadRequestRef.current += 1
    const previousFocus = previousFocusRef.current
    previousFocusRef.current = null

    setPreviewOpen(false)
    setPreviewLoadingState('idle')
    setPreviewError(null)
    revokeCurrentBlob()
    setBlobUrl(null)
    setPreviewCategory(null)
    setSelectedCitation(null)

    // Deferred so focus returns after the panel has been removed from the accessibility tree.
    setTimeout(() => previousFocus?.focus?.(), 0)
  }, [revokeCurrentBlob])

  const goToNextHighlight = useCallback(() => {
    setActiveHighlightIndex((index) => {
      const total = selectedCitation?.locations.length || 0
      return total === 0 ? index : (index + 1) % total
    })
  }, [selectedCitation])

  const goToPrevHighlight = useCallback(() => {
    setActiveHighlightIndex((index) => {
      const total = selectedCitation?.locations.length || 0
      return total === 0 ? index : (index - 1 + total) % total
    })
  }, [selectedCitation])

  const retryLoad = useCallback(() => {
    if (!selectedCitation) return
    const requestId = ++loadRequestRef.current
    revokeCurrentBlob()
    setBlobUrl(null)
    void loadDocument(selectedCitation, requestId)
  }, [selectedCitation, loadDocument, revokeCurrentBlob])

  // Reuses the already-loaded preview blob when available; otherwise fetches fresh and revokes
  // that one-off object URL immediately after triggering the save (the reused blobUrl stays alive
  // for the still-open preview instead).
  const downloadFallback = useCallback(async () => {
    if (!selectedCitation?.fileId) return
    try {
      let objectUrl = blobUrl
      let isFreshUrl = false
      if (!objectUrl) {
        const blob = await fetchDocumentBlob(selectedCitation)
        if (!(blob instanceof Blob) || blob.size === 0) return
        objectUrl = URL.createObjectURL(blob)
        isFreshUrl = true
      }
      const link = document.createElement('a')
      link.href = objectUrl
      link.download = selectedCitation.title
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      if (isFreshUrl) URL.revokeObjectURL(objectUrl)

      appInsights.trackEvent({
        name: EventType.CitationPreviewDownloaded,
        properties: {
          userId,
          fileId: selectedCitation.fileId,
          citationId: selectedCitation.citationId,
          fileName: selectedCitation.title,
          pageNumber: selectedCitation.pageNumber,
          previewCategory: previewCategory || getPreviewCategory(selectedCitation.title),
          timestamp: new Date().toISOString()
        }
      })
    } catch (error) {
      console.error('Failed to download citation document:', error)
    }
  }, [selectedCitation, blobUrl, fetchDocumentBlob, previewCategory, userId])

  return {
    selectedCitation,
    previewOpen,
    activeHighlightIndex,
    previewLoadingState,
    highlightedLocations: selectedCitation?.locations || [],
    previewError,
    previewCategory,
    blobUrl,
    openPreview,
    closePreview,
    goToNextHighlight,
    goToPrevHighlight,
    retryLoad,
    downloadFallback,
    handleViewerError
  }
}
